'use client';

import { For, SegmentGroup } from '@chakra-ui/react';
import * as React from 'react';

interface Item {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps extends SegmentGroup.RootProps {
  items: Array<string | Item>;
  variant?: 'default' | 'primary' | 'chip';
}

function normalize(items: Array<string | Item>): Item[] {
  return items.map((item) => {
    if (typeof item === 'string') return { value: item, label: item };
    return item;
  });
}

export const SegmentedControl = React.forwardRef<
  HTMLDivElement,
  SegmentedControlProps
>(function SegmentedControl(props, ref) {
  const { items, variant = 'default', ...rest } = props;
  const data = React.useMemo(() => normalize(items), [items]);

  if (variant === 'primary') {
    return (
      <SegmentGroup.Root
        ref={ref}
        p={1}
        rounded='2xl'
        bg='#fff'
        shadow='none'
        {...rest}
      >
        <SegmentGroup.Indicator rounded='xl' shadow='none' bg='primary' />
        <For each={data}>
          {(item) => (
            <SegmentGroup.Item
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              _checked={{ opacity: 1, color: 'primary.text' }}
              transition='50ms ease-in-out'
              opacity={0.5}
              h='40px'
              // bg='background'
            >
              <SegmentGroup.ItemText>{item.label}</SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          )}
        </For>
      </SegmentGroup.Root>
    );
  }

  if (variant === 'chip') {
    return (
      <SegmentGroup.Root
        ref={ref}
        px={1}
        rounded='2xl'
        shadow='none'
        gap={2}
        {...rest}
      >
        <SegmentGroup.Indicator
          rounded='full'
          top={0}
          shadow='none'
          bg='primary'
          height={rest.height}
          zIndex={1}
        />
        <For each={data}>
          {(item) => (
            <SegmentGroup.Item
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              rounded='full'
              bg='#fff'
              _checked={{ opacity: 1, color: '#fff' }}
              transition='50ms ease-in-out'
              opacity={0.5}
              height={rest.height}
              color={'#000'}
            >
              <SegmentGroup.ItemText zIndex={100}>
                {item.label}
              </SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          )}
        </For>
      </SegmentGroup.Root>
    );
  }

  return (
    <SegmentGroup.Root
      ref={ref}
      p={1}
      rounded='2xl'
      bg='background'
      color='text.muted'
      shadow='none'
      {...rest}
    >
      <SegmentGroup.Indicator rounded='xl' shadow='none' bg='background.card' />
      <For each={data}>
        {(item) => (
          <SegmentGroup.Item
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            _checked={{ opacity: 1 }}
            opacity={0.5}
            h='40px'
            color='text'
            // bg='background'
            whiteSpace='nowrap'
            flex={rest.w === 'full' ? 1 : undefined}
          >
            <SegmentGroup.ItemText>{item.label}</SegmentGroup.ItemText>
            <SegmentGroup.ItemHiddenInput />
          </SegmentGroup.Item>
        )}
      </For>
    </SegmentGroup.Root>
  );
});
